import React from 'react';

import useMainStore from '../../store/store';

const NOTIFY_SHOW_DELAY = 60 * 60 * 24 * 1000; // one day

const PWAContext = React.createContext({});

export function PWAContextProvider({ children }: { children: React.ReactNode }) {
  const setShowAppModal = useMainStore((s) => s.setShowAppModal);
  const setShowInstallPWA = useMainStore((state) => state.setShowInstallPWA);

  const deferredPWAInstallationPrompt = React.useRef(null);

  const UA = navigator.userAgent;
  const IOS = UA.match(/iPhone|iPad|iPod/);
  const ANDROID = UA.match(/Android/);

  // eslint-disable-next-line no-nested-ternary
  const platform = IOS ? 'ios' : ANDROID ? 'android' : 'unknown';
  const standalone = window.matchMedia('(display-mode: standalone)').matches;

  const openedAsPWA = !!(standalone || (IOS && !UA.match(/Safari/)));
  if (openedAsPWA) {
    console.log('openedAsPWA');
  }

  const iOSCanInstall = 'standalone' in window.navigator && !openedAsPWA;

  const openPWAInstallationPrompt = async () => {
    if (deferredPWAInstallationPrompt && deferredPWAInstallationPrompt.current !== null) {
      // @ts-ignore
      deferredPWAInstallationPrompt.current.prompt();
      // Wait for the user to respond to the prompt
      // @ts-ignore
      const { outcome } = await deferredPWAInstallationPrompt.current.userChoice;
      // Optionally, send analytics event with outcome of user choice
      console.log(`User response to the install prompt: ${outcome}`);
      // We've used the prompt, and can't use it again, throw it away
      deferredPWAInstallationPrompt.current = null;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt.');
      } else if (outcome === 'dismissed') {
        console.log('User dismissed the install prompt');
      }
    }
  };

  const onInstallAction = () => {
    if (platform === 'android') return openPWAInstallationPrompt();
    if (platform === 'ios') {
      setShowAppModal('iosPWA');
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    if (iOSCanInstall && (platform === 'ios' || platform === 'android')) {
      setShowInstallPWA(true);
    }

    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      // @ts-ignore
      deferredPWAInstallationPrompt.current = e;

      if (platform === 'ios' || platform === 'android') {
        setShowInstallPWA(true);
      }
    });

    window.addEventListener('appinstalled', () => {
      console.log('appinstalled');
    });
  }, [iOSCanInstall, platform, setShowInstallPWA]);

  return (
    <PWAContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        openPWAInstallationPrompt,
        onInstallAction,
        openedAsPWA,
        IOS,
        ANDROID,
      }}
    >
      {children}
    </PWAContext.Provider>
  );
}

export const usePWAContext = () => React.useContext(PWAContext);
